import './App.css';
import React, { Suspense, useState, useEffect, useRef } from 'react';
import { useProgress } from '@react-three/drei';
import Scene from './components/3d/Scene';
import Model from './components/3d/Model';
import Content from './components/content/Content';
import Preloader from './components/preloader/Preloader';
import Overlay from './components/overlay/Overlay';

import {
  preloadAllSounds,
  setMuteAllSounds,
  playEventSound9,
  playEventSound12,
  playEventSound13,
  playObjectSound
} from './utils/soundUtils';

// A component to bridge loading progress to the React state
function LoadingManager({ setLoading, setProgress }) {
  const [soundsReady, setSoundsReady] = useState(false);
  const [soundsLoading, setSoundsLoading] = useState(true);
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const { active, progress } = useProgress();

  // Lade Sounds
  useEffect(() => {
    setSoundsLoading(true);
    preloadAllSounds()
      .then(() => {
        setSoundsReady(true);
      })
      .catch(error => {
        console.error('Error loading sounds:', error);
      })
      .finally(() => {
        setSoundsLoading(false);
      });
  }, []);

  // Prüfe, ob alles geladen ist
  useEffect(() => {
    // Alles ist geladen, wenn:
    // 1. Die 3D-Modelle fertig sind (!active) UND
    // 2. Die Sounds entweder fertig geladen sind oder es beim Laden einen Fehler gab
    if (!active && !soundsLoading) {
      // Kurze Verzögerung für den Übergang
      const timer = setTimeout(() => {
        setLoading(false);
        setAssetsLoaded(true);
      }, 500);

      return () => clearTimeout(timer);
    } else {
      setLoading(true);
    }
  }, [active, soundsLoading, setLoading]);

  // Aktualisiere den Fortschritt
  useEffect(() => {
    // Kombiniere den Fortschritt von 3D und Sounds
    // Hier können Sie die Gewichtung anpassen, z.B. 80% für 3D, 20% für Sounds
    const weightedProgress = soundsLoading
      ? progress * 0.8  // Wenn Sounds noch laden, zeige max 80% Fortschritt
      : progress * 0.8 + 20; // Wenn Sounds fertig, füge die restlichen 20% hinzu

    setProgress(weightedProgress);
  }, [progress, soundsLoading, setProgress]);

  return null;
}

function App() {
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [showContent, setShowContent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentObjectIndex, setCurrentObjectIndex] = useState(0);
  const [allObjects, setAllObjects] = useState([]);
  const [muted, setMuted] = useState(false);
  const [appStarted, setAppStarted] = useState(false);
  const [modelPath, setModelPath] = useState(`assets/models/CR.glb?t=${new Date().getTime()}`);
  const [showOverlay, setShowOverlay] = useState(false);

  const getScale = (isContentVisible) => {
    const minWidth = 320;
    const maxWidth = 1920;
    const minScale = 0.1;
    const maxScale = 0.2;

    // Adjust width based on whether the content panel is visible
    const containerWidth = isContentVisible ? window.innerWidth - 500 : window.innerWidth;

    const width = Math.max(minWidth, Math.min(containerWidth, maxWidth));
    const scale = minScale + ((width - minWidth) / (maxWidth - minWidth)) * (maxScale - minScale);
    return scale;
  };

  const [scale, setScale] = useState(getScale(showContent));
  const animationInterval = useRef(null); // To keep track of the animation interval

  // This effect handles the smooth scaling animation when 'showContent' changes.
  useEffect(() => {
    // Stop any previous animation before starting a new one.
    if (animationInterval.current) {
      clearInterval(animationInterval.current);
    }

    const startScale = scale;
    const endScale = getScale(showContent);
    const duration = 500; // Animation duration in ms, should match your CSS transition.
    const frameRate = 60; // Animate at 60 frames per second.
    const stepTime = 1000 / frameRate;
    const totalSteps = Math.round(duration / stepTime);
    let currentStep = 0;

    // Don't start an animation if the scale is already correct.
    if (Math.abs(startScale - endScale) < 0.0001) {
      return;
    }


    animationInterval.current = setInterval(() => {
      currentStep++;
      const progress = currentStep / totalSteps;

      // Use an easing function for a smoother effect (ease-out cubic).
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const newScale = startScale + (endScale - startScale) * easedProgress;

      setScale(newScale);

      if (currentStep >= totalSteps) {
        setScale(endScale); // Ensure the final value is precise.
        clearInterval(animationInterval.current);
      }
    }, stepTime);

    // Cleanup function to clear the interval when the component unmounts or deps change.
    return () => {
      if (animationInterval.current) {
        clearInterval(animationInterval.current);
      }
    };
    // 'scale' is a dependency, but including it would restart the animation on every frame.
    // We capture the initial 'scale' when the effect runs, which is the desired behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showContent]);

  // This effect handles window resizing.
  useEffect(() => {
    const handleResize = () => {

      // When the window is resized, we stop any ongoing animation
      // and immediately snap to the new correct scale.
      if (animationInterval.current) {
        clearInterval(animationInterval.current);
      }
      setScale(getScale(showContent));
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [showContent]);

  const handleHotspotClick = (hotspotName, index) => {

    setActiveHotspot(hotspotName);
    if (index !== undefined) {
      setCurrentObjectIndex(index);
    } else {
      // Fallback: Finde den Index anhand des Namens, falls nicht angegeben
      const foundIndex = allObjects.findIndex(obj => obj.children[0].name === hotspotName);

      if (foundIndex !== -1) {
        setCurrentObjectIndex(foundIndex);
      }
    }

    setShowContent(activeHotspot === hotspotName ? !showContent : true);
    setShowOverlay(false);


  }

  const handleNextObject = () => {
    if (allObjects.length > 0) {

      const nextIndex = (currentObjectIndex + 1) % allObjects.length;

      const nextHotspot = allObjects[nextIndex]?.name;
      if (nextHotspot) {
        setCurrentObjectIndex(nextIndex);
        setActiveHotspot(nextHotspot);
        playObjectSound(nextHotspot);
        // Dispatch event to Model.js to update the selected object
        window.dispatchEvent(new CustomEvent('selectObject', { detail: { objectName: nextHotspot } }));
      }
    }
  }

  const handlePrevObject = () => {
    if (allObjects.length > 0) {

      //playEventSound9();

      const prevIndex = (currentObjectIndex - 1 + allObjects.length) % allObjects.length;
      const prevHotspot = allObjects[prevIndex]?.name;
      if (prevHotspot) {
        setCurrentObjectIndex(prevIndex);
        setActiveHotspot(prevHotspot);
        playObjectSound(prevHotspot);
        // Dispatch event to Model.js to update the selected object
        window.dispatchEvent(new CustomEvent('selectObject', { detail: { objectName: prevHotspot } }));
      }
    }
  }

  const handleCloseContent = () => {
    playEventSound9();
    setShowContent(false);
    setActiveHotspot(null);
    // Dispatch event to reset objects in Model.js
    window.dispatchEvent(new Event('resetObjects'));
  };

  const muteAllSounds = () => {
    setMuted(!muted);
    setMuteAllSounds(!muted);
    //stopBackgroundSound();
  }


  return (
    <div className={`App`}>
      <div className="header">
        <img
          src="./assets/images/Icon.svg"
          alt="Crafted Reality Icon"
          onClick={() => {
            if (showContent) {
              setShowContent(false);
              setActiveHotspot(null);
              window.dispatchEvent(new Event('resetObjects'));
            }
          }}
          className="cr-icon"
          style={{ cursor: 'pointer' }}
        />
          <img
              src="./assets/images/Icon_Contact.svg"
              alt="Contact Icon"
              className="info-icon2"
              onClick={() => {
                  setShowOverlay(true);
                  playEventSound13();
              }}
              style={{ cursor: 'pointer' }}
          />
        <div className="header-right">
          <a className="logo" href="https://ambion.de" target="_blank" rel="noopener noreferrer">
            <img className="logo" src="./assets/images/Logo.png" alt="AMBION Logo" />
          </a>
          <img
            src="./assets/images/Icon_Contact.svg"
            alt="Contact Icon"
            className="info-icon"
            onClick={() => {
              setShowOverlay(true);
              playEventSound13();
            }}
            style={{ cursor: 'pointer' }}
          />
        </div>
      </div>
      <div className={`background ${showContent ? 'shifted' : ''}`}></div>
      <Preloader active={loading} progress={progress} onStart={() => setAppStarted(true)} />
      <div className={`scene-wrapper ${showContent ? 'shifted' : ''}`}>
        <Scene isObjectSelected={showContent}>
          <Model
            path={modelPath}
            scale={scale}
            onHotspotClick={handleHotspotClick}
            showContent={showContent}
            onObjectsLoaded={setAllObjects}

          />
        </Scene>
        <LoadingManager setLoading={setLoading} setProgress={setProgress} />

      </div>
      <Content
        isVisible={showContent}
        hotspotName={activeHotspot}
        onClose={handleCloseContent}
      />

      {/* Navigation Arrows */}
      <div className={`nav-arrows ${showContent && allObjects.length > 1 ? 'visible' : ''}`}>
        <button
          className={`nav-arrow nav-arrow--left ${currentObjectIndex === 0 ? 'disabled' : ''}`}
          onClick={handlePrevObject}
          disabled={!showContent || allObjects.length <= 1}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button
          className={`nav-arrow nav-arrow--right ${currentObjectIndex >= allObjects.length - 1 ? 'disabled' : ''}`}
          onClick={handleNextObject}
          disabled={!showContent || allObjects.length <= 1}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      {showOverlay && !showContent && <Overlay onClose={() => { setShowOverlay(false); playEventSound12() }} />}
      <div className="footer">

        {appStarted ? (
          <>
            <div className="footer-left-container">
              <button className="sound-button" onClick={() => muteAllSounds()}>
                {`${muted ? 'Ton an' : 'Ton aus'}`}
              </button>
              <a href="https://www.ambion.de/impressum-2/" target="_blank" rel="noopener noreferrer">
                Impressum
              </a>
              <a href="https://www.ambion.de/datenschutzerklaerung/" target="_blank" rel="noopener noreferrer">
                Datenschutz
              </a>
              <a href="https://www.ambion.de/kontakt/" target="_blank" rel="noopener noreferrer">
                Kontakt
              </a>
              <a href="https://www.ambion.de/" target="_blank" rel="noopener noreferrer">
                © AMBION GmbH {new Date().getFullYear()}
              </a>
            </div>
            <a href="https://ambion.de" target="_blank" rel="noopener noreferrer">
              <img className="logo" src="./assets/images/Logo.png" alt="AMBION Logo" />
            </a>
          </>
        ) : (
          <>
            <p></p>
            <p></p>
            <p></p>


            <a href="https://ambion.de" target="_blank" rel="noopener noreferrer">
              <img className="logo-preloader" src="./assets/images/Logo.png" alt="AMBION Logo" />
            </a>
          </>
        )}

      </div>

    </div>
  );
}

export default App;
